import { useEffect, useState } from "react";
import { apiFetch, TENANT } from "../api.js";
import type { Board } from "../api.js";
import { formatCT } from "../time.js";
import { DRAFT_MAX_AGE_DAYS, draftExpiresInDays } from "../../domain/drafts.js";
import { archivedCount, scenarioPrefix, sortScenarios, visibleScenarios } from "../scenarioList.js";

export interface DiffSummary {
  base: { scenarioId: string; name: string | null };
  target: { scenarioId: string; name: string; status: string };
  summary: {
    reassigned: number; rescheduled: number; reorderedOnly: number;
    projectsEarlier: number; projectsLater: number; hoursMoved: number;
  };
  projects: { projectId: string; fromEnd: string | null; toEnd: string | null; shiftBlocks: number }[];
  changes: { phaseId: string; phaseCode: string | null; step?: string; kinds: string[]; fromName: string | null; toName: string | null; endShiftBlocks: number | null }[];
}

/**
 * "No differences" and "could not compare" look the same in a summary of
 * zeroes, and a fresh copy is always the former. Say which.
 */
function isUnchanged(d: DiffSummary): boolean {
  const s = d.summary;
  return (
    s.reassigned === 0 && s.rescheduled === 0 && s.reorderedOnly === 0 &&
    s.projectsEarlier === 0 && s.projectsLater === 0 && s.hoursMoved === 0
  );
}

/**
 * The scenario bar. Previously a draft could be created and then never found
 * again — there was no switcher, so "new scenario" looked like it did nothing
 * while 30 drafts piled up in the database.
 */
export function ScenarioBar({
  board,
  onSwitch,
  onCreate,
  onPublished,
  autoDiff = 0,
}: {
  board: Board;
  onSwitch: (id: string) => void;
  onCreate: () => void;
  onPublished: () => void;
  /** Bumped by the caller to open the change summary — e.g. right after creating a scenario. */
  autoDiff?: number;
}) {
  const [showDiff, setShowDiff] = useState(false);
  const [diff, setDiff] = useState<DiffSummary | null>(null);
  const [against, setAgainst] = useState<string | undefined>();
  // What's in flight, not just whether something is — a bare boolean left
  // Publish with nothing to show for a ~30s solve but its own disabled state,
  // which is nearly invisible next to buttons with no other visual treatment
  // for it. Holding the label lets both the button that was clicked and a bar
  // that's always on screen (not just inside the diff panel) say what's
  // actually happening.
  const [busy, setBusy] = useState<string | null>(null);
  const current = board.scenarios.find((s) => s.scenarioId === board.scenarioId);
  const isDraft = current?.status === "draft";
  const isArchived = current?.status === "archived";
  // Past live schedules pile up, one per publish, so the picker leaves them out
  // unless asked. Remembered per browser; it falls back to hidden if storage is blocked.
  const [showArchived, setShowArchived] = useState<boolean>(() => {
    try { return localStorage.getItem("sentry.showArchivedSchedules") === "1"; } catch { return false; }
  });
  const toggleArchived = (on: boolean) => {
    setShowArchived(on);
    try { localStorage.setItem("sentry.showArchivedSchedules", on ? "1" : "0"); } catch { /* not remembered */ }
  };
  const isScratch = Boolean((current as any)?.isScratch);
  const expiresIn = current ? draftExpiresInDays(current as any, new Date()) : null;

  useEffect(() => { setDiff(null); setShowDiff(false); setAgainst(undefined); }, [board.scenarioId]);

  // Creating a scenario is the moment you most want to know what changed, so
  // the caller opens this rather than leaving it behind a button nobody found.
  useEffect(() => {
    if (autoDiff > 0) void loadDiff(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDiff]);

  async function loadDiff(target?: string) {
    setBusy("Comparing…");
    try {
      const qs = new URLSearchParams({ tenantId: String(TENANT) });
      if (target) qs.set("against", target);
      const r = await apiFetch(`/v1/sentry/scenarios/${board.scenarioId}/diff?${qs}`);
      setDiff(r.ok ? await r.json() : null);
      setShowDiff(true);
    } finally { setBusy(null); }
  }

  async function act(path: string, method: string, label: string, body?: unknown) {
    setBusy(label);
    try {
      const r = await apiFetch(`/v1/sentry/scenarios/${board.scenarioId}${path}`, {
        method, headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId: 6, ...(body as object) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { alert(j.error ?? `HTTP ${r.status}`); return false; }
      return true;
    } finally { setBusy(null); }
  }

  async function publish() {
    // The re-solve a publish can trigger is the ~30s a caller has no other
    // signal for — this is the one thing on the whole bar worth naming on its
    // own rather than folding into a generic "working…".
    setBusy("Publishing — re-solving the live schedule…");
    try {
      const r = await apiFetch(`/v1/sentry/scenarios/${board.scenarioId}/publish`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId: 6 }),
      });
      if (r.ok) { onPublished(); return; }
      // Same as `act()` below: a refusal (an admin-only rule, most often) is
      // information, not nothing. Left unhandled, the button just reset with
      // no explanation, which read as "the tool doesn't work" rather than
      // "you don't have the rights for this."
      const j = await r.json().catch(() => ({}));
      alert(j.error ?? `HTTP ${r.status}`);
    } finally { setBusy(null); }
  }

  // Newest first, published pinned to the top — 30 drafts is a lot to scan.
  const sorted = sortScenarios(board.scenarios);
  const listed = visibleScenarios(sorted, board.scenarioId, showArchived);
  const nArchived = archivedCount(board.scenarios);

  return (
    <>
      <div className="scenbar">
        <span className="lab">Scenario</span>
        <select value={board.scenarioId} onChange={(e) => onSwitch(e.target.value)}>
          {listed.map((s) => (
            <option key={s.scenarioId} value={s.scenarioId}>
              {scenarioPrefix(s as any)}{s.name}
              {s.createdAt ? ` (${formatCT(s.createdAt)})` : ""}
            </option>
          ))}
        </select>
        {nArchived > 0 && (
          <label className="muted" style={{ fontSize: 12, display: "inline-flex", gap: 5, alignItems: "center", whiteSpace: "nowrap" }}
            title="Past live schedules. Kept, but hidden so the list stays short.">
            <input type="checkbox" checked={showArchived} onChange={(e) => toggleArchived(e.target.checked)} />
            Show archived ({nArchived})
          </label>
        )}
        {isDraft ? (
          <span className="chip draft"
            title={`A draft nobody works in for ${DRAFT_MAX_AGE_DAYS} days is removed automatically. Working in it, or moving work, starts the clock again.`}>
            {isScratch ? "working copy — overwritten by the next run" : "draft — not live"}
            {expiresIn !== null && ` · ${expiresIn === 0 ? "removed soon" : `removed in ${expiresIn}d if unused`}`}
          </span>
        ) : isArchived ? (
          <span className="chip low" title="This was the live schedule until a newer one was published. It is kept as history and can be compared with, but not edited.">
            archived — a past live schedule
          </span>
        ) : (
          <span className="chip live">live schedule</span>
        )}
        <span className="spacer" />
        {busy && <span className="muted">{busy}</span>}
        {isDraft && (
          <>
            <button className="btn" disabled={!!busy} onClick={() => (showDiff ? setShowDiff(false) : void loadDiff(against))}>
              {showDiff ? "Hide changes" : "What changes?"}
            </button>
            {isScratch && (
              <button className="btn" disabled={!!busy} onClick={async () => {
                const name = prompt("Name this scenario so the next run doesn't overwrite it:");
                if (name && await act("/keep", "POST", "Keeping…", { name })) onPublished();
              }}>Keep</button>
            )}
            <button className="btn" disabled={!!busy} onClick={async () => {
              if (confirm(`Delete the draft “${current?.name ?? "this scenario"}”? This cannot be undone. The live schedule is not affected.`)
                && await act("", "DELETE", "Deleting…")) onPublished();
            }}>Delete draft</button>
            <button className="btn on" disabled={!!busy} onClick={() => void publish()}>
              {busy?.startsWith("Publishing") ? "Publishing…" : "Publish"}
            </button>
          </>
        )}
        <button className="btn" disabled={!!busy} onClick={onCreate}>New scenario</button>
      </div>

      {showDiff && (
        <div className="diff">
          {/* Which two are being compared, and a way to change it. The endpoint
              always supported ?against=; there was simply no way to say so. */}
          <div className="diff-head">
            <span className="mono">{current?.name ?? "this scenario"}</span>
            <span className="muted">compared with</span>
            <select
              value={against ?? diff?.base.scenarioId ?? ""}
              disabled={!!busy}
              onChange={(e) => { setAgainst(e.target.value); void loadDiff(e.target.value); }}
            >
              {sorted
                .filter((s) => s.scenarioId !== board.scenarioId)
                .map((s) => (
                  <option key={s.scenarioId} value={s.scenarioId}>
                    {s.status === "published" || s.status === "archived" ? scenarioPrefix(s as any) : ""}{s.name}
                  </option>
                ))}
            </select>
            {busy && <span className="muted">{busy}</span>}
          </div>

          {!diff ? (
            <div className="muted" style={{ padding: 12 }}>
              Nothing to compare against — this is the only scenario.
            </div>
          ) : isUnchanged(diff) ? (
            <div className="muted" style={{ padding: 12 }}>
              Identical so far. Nothing has been moved in this scenario yet — drag work on the
              schedule, or press ⌘K and describe what you want changed.
            </div>
          ) : (
            <>
              <div className="diff-sum">
                <span>Publishing this would:</span>
                <b>{diff.summary.reassigned}</b> reassignments
                <b>{diff.summary.hoursMoved}</b> h changing hands
                <b className={diff.summary.projectsEarlier ? "good" : ""}>{diff.summary.projectsEarlier}</b> projects earlier
                <b className={diff.summary.projectsLater ? "crit" : ""}>{diff.summary.projectsLater}</b> later
              </div>
              <div className="diff-cols">
                <div>
                  <h4>Projects that move</h4>
                  {diff.projects.slice(0, 10).map((p) => (
                    <div className="diff-row" key={p.projectId}>
                      <span className="mono">{p.projectId}</span>
                      <span className="mono muted">{p.fromEnd} → {p.toEnd}</span>
                      <span className={`mono ${p.shiftBlocks < 0 ? "good" : "crit"}`}>
                        {p.shiftBlocks < 0 ? "" : "+"}{Math.round(p.shiftBlocks / 2)}d
                      </span>
                    </div>
                  ))}
                  {!diff.projects.length && <div className="muted">No project finishes on a different date.</div>}
                </div>
                <div>
                  <h4>Work changing hands</h4>
                  {diff.changes.filter((c) => c.kinds.includes("reassigned")).slice(0, 10).map((c) => (
                    <div className="diff-row" key={c.phaseId}>
                      <span className="mono">{c.step ?? c.phaseCode ?? c.phaseId}</span>
                      <span className="mono muted">{c.fromName ?? "pool"} → {c.toName ?? "pool"}</span>
                    </div>
                  ))}
                  {!diff.changes.some((c) => c.kinds.includes("reassigned")) && (
                    <div className="muted">Nobody's work changes owner.</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
